import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, FileText, Check, Target, ArrowLeft } from "lucide-react";
import axios from "axios";
import ALL_ROLES from "@/data/roles";
import SearchableRoleDropdown from "@/components/SearchableRoleDropdown";
import SkillsInput from "@/components/SkillsInput";
import { trackLead, trackResumeUploadComplete } from "@/utils/metaPixel";
import { trackResumeUpload } from "@/utils/googleAnalytics";
import { LEVEL_LABELS, deriveLevel } from "@/data/profile";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntakePage() {
  // React Router keeps the previous page's scroll offset on navigation, so
  // arriving from a scrolled-down page could open the intake card cut off under
  // the fixed navbar. On every arrival, reset to top and drop the cursor into the
  // first field (Years) — preventScroll so the focus doesn't undo the reset.
  useEffect(() => {
    window.scrollTo(0, 0);
    yearsRef.current?.focus({ preventScroll: true });
  }, []);

  const [step, setStep] = useState(1);
  const [targetRole, setTargetRole] = useState("");
  const [years, setYears] = useState("");
  const [levelOverride, setLevelOverride] = useState("");   // "" = use derived
  const [primarySkills, setPrimarySkills] = useState([]);
  const [currentTitle, setCurrentTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const yearsRef = useRef(null);
  const { getHeaders, setUser, user } = useAuth();
  const navigate = useNavigate();

  const derivedLevel = deriveLevel(years);
  const effectiveLevel = levelOverride || derivedLevel;

  const fetchRoles = async (q) => {
    try {
      const res = await axios.get(`${API}/roles/search`, { params: { q, limit: 20 }, headers: getHeaders() });
      return res.data.results || [];
    } catch { return []; }
  };

  const profilePayload = () => ({
    target_role: targetRole,
    years_experience: years === "" ? null : Number(years),
    level: effectiveLevel || null,
    role_category: "",            // inferred from target_role server-side
    primary_skills: primarySkills,
    current_title: currentTitle,
  });

  const leadPayload = (eventStage, leadSource) => ({
    content_category: targetRole || "general",
    event_stage: eventStage,
    funnel: "resume_analysis_frontend",
    lead_source: leadSource,
    target_role: targetRole || "",
    years_experience: years === "" ? null : Number(years),
    level: effectiveLevel || "",
  });

  const handleContinue = async () => {
    if (!targetRole) { toast.error("Please select a target role"); return; }
    if (years === "" || isNaN(Number(years)) || Number(years) < 0) {
      toast.error("Please enter your years of experience"); return;
    }
    // Persist the profile (best-effort; not critical for anonymous users).
    try {
      await axios.put(`${API}/user/profile`, profilePayload(), { headers: getHeaders() });
      if (user) setUser((prev) => ({ ...prev, target_role: targetRole }));
    } catch (err) { /* non-blocking */ }
    trackLead("Free ATS Score Profile Submit", leadPayload("profile_submit", "intake_profile"));
    setStep(2);
  };

  const handleFileChange = (e) => validateAndSet(e.target.files[0]);
  const handleDrop = (e) => { e.preventDefault(); validateAndSet(e.dataTransfer.files[0]); };
  const validateAndSet = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    if (!f.name.match(/\.(pdf|docx)$/i)) { toast.error("Only PDF and DOCX accepted"); return; }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/resume/upload`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Resume uploaded successfully!");
      const resumeUploadPayload = leadPayload("resume_upload_success", "intake_upload");
      trackLead("Free ATS Score Resume Upload", resumeUploadPayload);
      trackResumeUploadComplete(resumeUploadPayload);
      trackResumeUpload(file.name);
      // Carry the structured profile to the chat → analysis step.
      navigate("/chat", { state: { resumeId: res.data.id, targetRole, profile: profilePayload() } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ap-root min-h-screen pt-24 pb-24" data-testid="intake-page">
      <div className="ap-section max-w-2xl">
        {/* Progress */}
        <div className="flex items-center gap-3 mb-10 ap-rise ap-rise-1">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all"
            style={step >= 1
              ? { background: "var(--ap-fire)", color: "#fff", boxShadow: "var(--ap-glow)" }
              : { background: "var(--ap-canvas-2)", color: "var(--ap-muted)" }}
          >1</div>
          <div
            className="flex-1 h-1 rounded-full transition-all"
            style={step >= 2 ? { background: "var(--ap-fire)" } : { background: "var(--ap-line)" }}
          />
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all"
            style={step >= 2
              ? { background: "var(--ap-fire)", color: "#fff", boxShadow: "var(--ap-glow)" }
              : { background: "var(--ap-canvas-2)", color: "var(--ap-muted)" }}
          >2</div>
        </div>

        {step === 1 && (
          <Card className="ap-card border-0 ap-rise ap-rise-2 overflow-hidden" data-testid="role-selection-card">
            <CardHeader className="pt-7">
              <span
                className="ap-eyebrow inline-flex items-center gap-2 mb-3"
              >
                <Target className="w-4 h-4" /> Step 1 · Your profile
              </span>
              <CardTitle className="ap-display text-2xl font-extrabold text-[var(--ap-ink)] leading-tight">
                Let's <span className="ap-grad-text">calibrate</span> your profile
              </CardTitle>
              <p className="text-[var(--ap-muted)] text-[15px] mt-2 leading-relaxed">A few details so we score your resume the way a recruiter for your level and field would.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Years of experience → derived level (pills) */}
              <div>
                <label className="block text-sm font-semibold text-[var(--ap-ink)] mb-2">Years of experience</label>
                <input
                  ref={yearsRef}
                  type="number" min="0" max="50" value={years}
                  onChange={(e) => { setYears(e.target.value); setLevelOverride(""); }}
                  placeholder="e.g. 4"
                  className="w-28 px-3.5 py-3 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white outline-none focus:border-[var(--ap-violet)] focus:ring-4 focus:ring-[var(--ap-violet-soft)] transition-all"
                  data-testid="intake-years"
                />
                {derivedLevel && (
                  <div className="flex flex-wrap gap-2 mt-3.5" data-testid="intake-level">
                    {Object.entries(LEVEL_LABELS).map(([k, v]) => (
                      <button
                        type="button" key={k} onClick={() => setLevelOverride(k)}
                        className="px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all"
                        style={effectiveLevel === k
                          ? { background: "var(--ap-violet)", color: "#fff", borderColor: "var(--ap-violet)" }
                          : { background: "#fff", color: "var(--ap-muted)", borderColor: "var(--ap-line)" }}
                      >
                        {v}{k === derivedLevel ? " · suggested" : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Target role title */}
              <div>
                <label className="block text-sm font-semibold text-[var(--ap-ink)] mb-2">Target job title</label>
                <SearchableRoleDropdown
                  roles={ALL_ROLES}
                  fetchOptions={fetchRoles}
                  allowCustom
                  value={targetRole}
                  onChange={setTargetRole}
                  placeholder="Select your target role..."
                  searchPlaceholder="Search roles... e.g. Backend Developer, Data Analyst"
                  testId="intake-role-dropdown"
                />
              </div>

              {/* Primary skills / stack (ordered) — with autosuggest */}
              <div>
                <label className="block text-sm font-semibold text-[var(--ap-ink)] mb-2">Top skills / stack <span className="text-[var(--ap-muted)] font-normal">(in priority order)</span></label>
                <SkillsInput value={primarySkills} onChange={setPrimarySkills} role={targetRole} testId="intake-skills" />
              </div>

              {/* Optional current title */}
              <div>
                <label className="block text-sm font-semibold text-[var(--ap-ink)] mb-2">Current / most-recent title <span className="text-[var(--ap-muted)] font-normal">(optional)</span></label>
                <input
                  type="text" value={currentTitle}
                  onChange={(e) => setCurrentTitle(e.target.value)}
                  placeholder="e.g. Solution Engineer"
                  className="w-full px-3.5 py-3 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white outline-none focus:border-[var(--ap-violet)] focus:ring-4 focus:ring-[var(--ap-violet-soft)] transition-all"
                  data-testid="intake-current-title"
                />
              </div>

              {targetRole && years !== "" ? (
                <button
                  onClick={handleContinue}
                  disabled={!targetRole || years === ""}
                  className="ap-btn ap-btn-fire ap-btn-lg w-full mt-2"
                  data-testid="continue-btn"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleContinue}
                  disabled={!targetRole || years === ""}
                  className="ap-btn ap-btn-lg w-full mt-2 cursor-not-allowed"
                  style={{ background: "var(--ap-canvas-2)", color: "var(--ap-muted)" }}
                  data-testid="continue-btn"
                >
                  Continue
                </button>
              )}
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="ap-card border-0 ap-rise ap-rise-2 overflow-hidden" data-testid="resume-upload-card">
            <CardHeader className="pt-7">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="ap-chip" style={{ color: "var(--ap-green)", borderColor: "rgba(22,163,74,.25)", background: "rgba(22,163,74,.08)" }}>
                  <Check className="w-4 h-4" />
                  {targetRole}{effectiveLevel ? ` · ${LEVEL_LABELS[effectiveLevel]}` : ""}
                </span>
              </div>
              <CardTitle className="ap-display text-2xl font-extrabold text-[var(--ap-ink)] leading-tight">
                Upload Your <span className="ap-grad-text">Resume</span>
              </CardTitle>
              <p className="text-[var(--ap-muted)] text-[15px] mt-2">PDF or DOCX, max 5MB</p>
            </CardHeader>
            <CardContent>
              <div
                onClick={() => fileRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all"
                style={file
                  ? { borderColor: "rgba(22,163,74,.5)", background: "rgba(22,163,74,.05)" }
                  : { borderColor: "var(--ap-line)", background: "var(--ap-canvas)" }}
                data-testid="upload-dropzone"
              >
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="w-11 h-11 text-[var(--ap-green)]" />
                    <p className="text-[var(--ap-ink)] font-semibold text-sm">{file.name}</p>
                    <p className="text-[var(--ap-muted)] text-xs">{(file.size / 1024).toFixed(0)} KB</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-1" style={{ background: "var(--ap-violet-soft)" }}>
                      <Upload className="w-7 h-7 text-[var(--ap-violet)]" />
                    </div>
                    <p className="text-[var(--ap-ink)] font-semibold text-sm">Click to upload or drag & drop</p>
                    <p className="text-[var(--ap-muted)] text-xs">PDF, DOCX up to 5MB</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={handleFileChange} className="hidden" data-testid="file-input" />
              </div>

              {file && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="ap-btn ap-btn-fire ap-btn-lg w-full mt-6 uppercase tracking-wider disabled:opacity-50"
                  data-testid="upload-submit-btn"
                >
                  {uploading ? "UPLOADING..." : "UPLOAD & CONTINUE"}
                </button>
              )}

              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 mx-auto mt-5 text-[var(--ap-muted)] text-sm font-medium hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer transition-colors"
                data-testid="back-to-role-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Edit profile
              </button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
